import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';

const InjuryHistoryDialog = ({ open, onClose, player, teamId, onUpdated }) => {
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    startUtc: '',
    endUtc: '',
    notes: ''
  });

  const loadInjuries = async () => {
    if (!player?.id || !teamId) return;

    setLoading(true);
    setError('');
    try {
      const data = await unifiedApi.get(`/api/UserTeam/${player.id}/${teamId}/injuries`);
      setInjuries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load injury history:', err);
      setError('Failed to load injury history');
      setInjuries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadInjuries();
    } else {
      setInjuries([]);
      setError('');
      setEditingId(null);
      setIsAdding(false);
      setFormData({ startUtc: '', endUtc: '', notes: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, player, teamId]);

  const handleAfterChange = async () => {
    await loadInjuries();
    if (onUpdated) {
      onUpdated();
    }
  };

  const handleStartAdd = () => {
    setIsAdding(true);
    setFormData({
      startUtc: new Date().toISOString().slice(0, 10),
      endUtc: '',
      notes: ''
    });
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setFormData({ startUtc: '', endUtc: '', notes: '' });
  };

  const handleSaveNew = async () => {
    if (!player?.id || !teamId) return;
    if (!formData.startUtc) {
      setError('Start date is required');
      return;
    }

    setError('');
    try {
      const payload = {
        startUtc: new Date(formData.startUtc).toISOString(),
        endUtc: formData.endUtc ? new Date(formData.endUtc).toISOString() : null,
        notes: formData.notes || null
      };

      await unifiedApi.post(`/api/UserTeam/${player.id}/${teamId}/injuries`, payload);
      setIsAdding(false);
      setFormData({ startUtc: '', endUtc: '', notes: '' });
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to create injury period:', err);
      setError('Failed to create injury period');
    }
  };

  const handleStartEdit = (injury) => {
    setEditingId(injury.id);
    setFormData({
      startUtc: new Date(injury.startUtc).toISOString().slice(0, 10),
      endUtc: injury.endUtc ? new Date(injury.endUtc).toISOString().slice(0, 10) : '',
      notes: injury.notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ startUtc: '', endUtc: '', notes: '' });
  };

  const handleSaveEdit = async (injuryId) => {
    if (!player?.id || !teamId) return;
    if (!formData.startUtc) {
      setError('Start date is required');
      return;
    }

    setError('');
    try {
      const payload = {
        id: injuryId,
        startUtc: new Date(formData.startUtc).toISOString(),
        endUtc: formData.endUtc ? new Date(formData.endUtc).toISOString() : null,
        notes: formData.notes || null
      };

      await unifiedApi.put(`/api/UserTeam/${player.id}/${teamId}/injuries/${injuryId}`, payload);
      setEditingId(null);
      setFormData({ startUtc: '', endUtc: '', notes: '' });
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to update injury period:', err);
      setError('Failed to update injury period');
    }
  };

  const handleDelete = async (injuryId) => {
    if (!player?.id || !teamId) return;
    if (!window.confirm('Delete this injury period?')) return;

    setError('');
    try {
      await unifiedApi.delete(`/api/UserTeam/${player.id}/${teamId}/injuries/${injuryId}`);
      await handleAfterChange();
    } catch (err) {
      console.error('Failed to delete injury period:', err);
      setError('Failed to delete injury period');
    }
  };

  const formatDate = (utcString) => {
    if (!utcString) return '-';
    return new Date(utcString).toLocaleDateString();
  };

  const playerName = player?.fullName || `${player?.firstName || ''} ${player?.lastName || ''}`.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Injury History - {playerName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage injury periods for this player
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Injury Periods
              </Typography>
              {!isAdding && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleStartAdd}
                >
                  Add Injury Period
                </Button>
              )}
            </Box>

            {isAdding && (
              <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  New Injury Period
                </Typography>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={formData.startUtc}
                      onChange={(e) => setFormData({ ...formData, startUtc: e.target.value })}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={formData.endUtc}
                      onChange={(e) => setFormData({ ...formData, endUtc: e.target.value })}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText="Leave empty if injury is ongoing"
                    />
                  </Stack>
                  <TextField
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    multiline
                    rows={2}
                    fullWidth
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveNew}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={handleCancelAdd}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {injuries.length === 0 && !isAdding ? (
              <Alert severity="info">No injury periods found for this player.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {injuries.map((injury) => (
                      <TableRow key={injury.id} hover>
                        <TableCell>
                          {editingId === injury.id ? (
                            <TextField
                              type="date"
                              value={formData.startUtc}
                              onChange={(e) => setFormData({ ...formData, startUtc: e.target.value })}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : (
                            formatDate(injury.startUtc)
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === injury.id ? (
                            <TextField
                              type="date"
                              value={formData.endUtc}
                              onChange={(e) => setFormData({ ...formData, endUtc: e.target.value })}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />
                          ) : (
                            formatDate(injury.endUtc) || 'Ongoing'
                          )}
                        </TableCell>
                        <TableCell>
                          {editingId === injury.id ? (
                            <TextField
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              size="small"
                              multiline
                            />
                          ) : (
                            injury.notes || '-'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {editingId === injury.id ? (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSaveEdit(injury.id)}
                                >
                                  <SaveIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEdit(injury)}
                                  disabled={editingId !== null || isAdding}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(injury.id)}
                                  disabled={editingId !== null || isAdding}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InjuryHistoryDialog;
